import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Grid,
  Stack,
  TextField,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  InputAdornment,
  Divider,
  Button,
} from "@mui/material";

import {
  Search,
  TrendingUp,
  TrendingDown,
  AccountBalanceWallet,
  Print,
  CalendarMonth,
  Category,
  Payment,
  ArrowForward
} from "@mui/icons-material";

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";

import { db, auth } from "../../firebase";

function Incomeexpreport() {
  const [loading, setLoading] = useState(true);
  const [schoolId, setSchoolId] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");

  // State handles for Date Range Filtration
  const [fromDate, setFromDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0] // First day of current month
  );
  const [toDate, setToDate] = useState(
    new Date().toISOString().split("T")[0] // Current Day
  );

  // States holding structural segmented data arrays
  const [openingBalance, setOpeningBalance] = useState(5089.00);
  const [feeIncomes, setFeeIncomes] = useState([]);
  const [otherIncomes, setOtherIncomes] = useState([]);
  const [inventoryIncomes, setInventoryIncomes] = useState([]);
  const [walletIncomes, setWalletIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenses1, setExpenses1] = useState([]);

  // Pagination setups
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ================= INITIAL HOOK =================
  useEffect(() => {
    getInitialData();
  }, [fromDate, toDate]); // Re-fetches dynamically whenever either date point transitions

  const getInitialData = async () => {
    try {
      if (auth.currentUser) {
        const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
        const sId = userSnap.data()?.schoolId;
        setSchoolId(sId);
      }
      await fetchDaybookData();
    } catch (e) {
      console.error("Initialization breakdown error:", e);
      setLoading(false);
    }
  };

  // ================= COMPLETE DATA INGESTION & ROLLING BALANCES =================
  const fetchDaybookData = async () => {
    setLoading(true);
    try {
      const BASE_DATE = "2026-07-01";
      const INITIAL_OPENING_BASE = 5089.00;

      // 1. DEFINE FIRESTORE QUERIES
      const feeStructureQ = query(collection(db, "fee_structures"));
      const historicalIncomeQ = query(collection(db, "income"));
      const historicalExpenseQ = query(collection(db, "expenses"), where('dayBook', '==', 'Yes'));
      const historicalExpenseQ1 = query(collection(db, "expenses"), where('dayBook', '==', 'No1'));
      const inventorySalesQ = query(collection(db, "studentInventory"), where('payMode', '!=', 'Wallet'));
      const walletTransactionsQ = query(collection(db, "wallet_transactions"));

      // 2. EXECUTE BATCH ASYNC PROMISES
      const [
        feeStructuresSnap,
        directIncomeSnap,
        expenseSnap,
        expenseSnap1,
        inventorySalesSnap,
        walletSnap
      ] = await Promise.all([
        getDocs(feeStructureQ),
        getDocs(historicalIncomeQ),
        getDocs(historicalExpenseQ),
        getDocs(historicalExpenseQ1),
        getDocs(inventorySalesQ),
        getDocs(walletTransactionsQ)
      ]);

      // 3. CALCULATE CUMULATIVE HISTORICAL ROLLING BALANCE (Before 'fromDate')
      let midPeriodIncome = 0;
      let midPeriodExpense = 0;

      if (fromDate > BASE_DATE) {
        // Historical Fees
        feeStructuresSnap.docs.forEach(docSnap => {
          const history = docSnap.data().paymentHistory || [];
          history.forEach(p => {
            if (p.date && p.date >= BASE_DATE && p.date < fromDate) {
              midPeriodIncome += parseFloat(p.paidAmount || 0);
            }
          });
        });

        // Historical Direct Income
        directIncomeSnap.docs.forEach(docSnap => {
          const d = docSnap.data();
          if (d.date && d.date >= BASE_DATE && d.date < fromDate) {
            midPeriodIncome += parseFloat(d.amount || 0);
          }
        });

        // Historical Student Inventory Sales
        inventorySalesSnap.docs.forEach(docSnap => {
          const d = docSnap.data();
          if (d.issueDate && d.issueDate >= BASE_DATE && d.issueDate < fromDate) {
            midPeriodIncome += parseFloat(d.grandTotal || 0);
          }
        });

        // Historical Wallet Topups
        walletSnap.docs.forEach(docSnap => {
          const d = docSnap.data();
          const tDate = d.dateTime;
          if (tDate && tDate >= BASE_DATE && tDate < fromDate) {
            if (d.type === 'Credit' || d.type === 'deposit') {
              midPeriodIncome += parseFloat(d.amount || 0);
            }
          }
        });

        // Historical DayBook Expenses
        expenseSnap.docs.forEach(docSnap => {
          const d = docSnap.data();
          if (d.date && d.date >= BASE_DATE && d.date < fromDate) {
            midPeriodExpense += parseFloat(d.amount || 0);
          }
        });
      }

      let calculatedOpening = INITIAL_OPENING_BASE;
      if (fromDate > BASE_DATE) {
        calculatedOpening = INITIAL_OPENING_BASE + midPeriodIncome - midPeriodExpense;
      }
      setOpeningBalance(calculatedOpening);

      // 4. EXTRACT TRANSACTIONS WITHIN SELECTED RANGE [fromDate -> toDate]
      const currentDayFees = [];
      feeStructuresSnap.docs.forEach(docSnap => {
        const studentData = docSnap.data();
        const history = studentData.paymentHistory || [];
        history.forEach(payment => {
          if (payment.date && payment.date >= fromDate && payment.date <= toDate) {
            currentDayFees.push({
              id: `${docSnap.id}_${payment.timestamp || Math.random()}`,
              date: payment.date,
              type: "Income",
              name: studentData.fullName || "Student Fee Payment",
              desc: payment.note ? `Note: ${payment.note}` : "School Fee Collection",
              mode: payment.payMode || "Cash",
              amount: parseFloat(payment.paidAmount || 0),
              cat: "Fee Payment"
            });
          }
        });
      });

      const currentDayIncomes = [];
      directIncomeSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.date && data.date >= fromDate && data.date <= toDate) {
          currentDayIncomes.push({
            id: docSnap.id,
            date: data.date,
            type: "Income",
            name: data.personName || "Other Source",
            desc: data.description || "Direct Inward Income",
            mode: data.paymentMethod || "Direct Transfer",
            amount: parseFloat(data.amount || 0),
            cat: "Direct Income"
          });
        }
      });

      const currentDayInventory = [];
      inventorySalesSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.issueDate && data.issueDate >= fromDate && data.issueDate <= toDate) {
          currentDayInventory.push({
            id: docSnap.id,
            date: data.issueDate,
            type: "Income",
            name: data.studentName || "Inventory Assignment",
            desc: `Issued ${data.items?.length || 0} items to student`,
            mode: data.payMode || data.paymentSource || "Direct",
            amount: parseFloat(data.grandTotal || 0),
            cat: "Inventory Sale"
          });
        }
      });

      const currentDayWallet = [];
      walletSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        const tDate = data.dateTime;
        if (tDate && tDate >= fromDate && tDate <= toDate && (data.type === 'deposit' || data.type === 'Credit')) {
          currentDayWallet.push({
            id: docSnap.id,
            date: tDate,
            type: "Income",
            name: data.studentName || "Student Wallet",
            desc: data.description || "Wallet Topup Deposit",
            mode: data.payMode || "Wallet/Cash",
            amount: parseFloat(data.amount || 0),
            cat: "Wallet Topup"
          });
        }
      });

      const currentDayExpenses = [];
      expenseSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.date && data.date >= fromDate && data.date <= toDate) {
          currentDayExpenses.push({
            id: docSnap.id,
            date: data.date,
            type: "Expense",
            name: data.personName || "Vendor/Service",
            desc: data.description || "DayBook Ledger Debit Outflow Entry",
            mode: data.payMode || "Cash",
            amount: parseFloat(data.amount || 0),
            cat: "DayBook Expense (Yes)"
          });
        }
      });

      const currentDayExpenses1 = [];
      expenseSnap1.docs.forEach(docSnap => {
        const data = docSnap.data();
        if (data.date && data.date >= fromDate && data.date <= toDate) {
          currentDayExpenses1.push({
            id: docSnap.id,
            date: data.date,
            type: "Expense",
            name: data.personName || "Vendor/Service",
            desc: data.description || "Non-DayBook Debit Outflow Entry",
            mode: data.payMode || "Online",
            amount: parseFloat(data.amount || 0),
            cat: "Expense (DayBook No)"
          });
        }
      });

      // Sort chronological streams inside assignment arrays
      const sorter = (a, b) => new Date(b.date) - new Date(a.date);
      
      setFeeIncomes(currentDayFees.sort(sorter));
      setOtherIncomes(currentDayIncomes.sort(sorter));
      setInventoryIncomes(currentDayInventory.sort(sorter));
      setWalletIncomes(currentDayWallet.sort(sorter));
      setExpenses(currentDayExpenses.sort(sorter));
      setExpenses1(currentDayExpenses1.sort(sorter));

    } catch (e) {
      console.error("Critical calculation breakdown:", e);
    } finally {
      setLoading(false);
    }
  };

  // ================= MERGE & CHRONOLOGICAL ORDERING =================
  const combinedData = useMemo(() => {
    return [
      ...feeIncomes,
      ...otherIncomes,
      ...inventoryIncomes,
      ...walletIncomes,
      ...expenses,
      ...expenses1
    ].sort((a, b) => new Date(a.date) - new Date(b.date)); // Ascending chronological view inside tables
  }, [feeIncomes, otherIncomes, inventoryIncomes, walletIncomes, expenses, expenses1]);

  // ================= CLIENT FILTERS PROCESSOR =================
  const filteredData = useMemo(() => {
    return combinedData.filter((item) => {
      const matchesSearch =
        item.name?.toLowerCase().includes(search.toLowerCase()) ||
        item.desc?.toLowerCase().includes(search.toLowerCase()) ||
        item.cat?.toLowerCase().includes(search.toLowerCase());

      const matchesType =
        typeFilter === "All" ? true : item.type === typeFilter;

      const matchesCategory =
        categoryFilter === "All" ? true : item.cat === categoryFilter;

      return matchesSearch && matchesType && matchesCategory;
    });
  }, [combinedData, search, typeFilter, categoryFilter]);

  // ================= DYNAMIC MATH AGGREGATIONS =================
  const totalIncome = useMemo(() => {
    return filteredData
      .filter((item) => item.type === "Income")
      .reduce((sum, item) => sum + item.amount, 0);
  }, [filteredData]);

  const totalExpense = useMemo(() => {
    return filteredData
      .filter((item) => item.type === "Expense")
      .reduce((sum, item) => sum + item.amount, 0);
  }, [filteredData]);

  const netPeriodChange = totalIncome - totalExpense;
  const computedClosingBalance = openingBalance + netPeriodChange;

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" mt={12} minHeight="40vh">
        <CircularProgress size={36} thickness={4.5} />
      </Box>
    );
  }

  return (
    <Box p={2} bgcolor="#f8fafc" minHeight="100vh">
      <style>
        {`
          @media print {
            body { background: #fff; color: #000; font-size: 11px; }
            .no-print { display: none !important; }
            .print-full-width { width: 100% !important; max-width: 100% !important; }
            tr { page-break-inside: avoid; }
            .MuiPaper-root { border: none !important; box-shadow: none !important; }
          }
        `}
      </style>

      {/* ================= COMPACT HEADER PANEL ================= */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 1.5,
          borderRadius: 2,
          background: "linear-gradient(135deg, #0f172a, #1e293b)",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={800} letterSpacing="-0.5px" sx={{ fontSize: "1.25rem" }}>
            Comprehensive Finance Daybook
          </Typography>
          <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mt: 0.2 }}>
            Report Period: <strong>{fromDate}</strong> <span style={{color: '#64748b'}}>to</span> <strong>{toDate}</strong> | Active Offset Base: 2026-07-01
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} className="no-print" alignItems="center" flexWrap="wrap" gap={{xs: 1, sm: 0}}>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPage(0);
            }}
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.07)",
              borderRadius: 1,
              input: { color: "white", py: 1, fontSize: "0.85rem", width: '110px' },
              label: { color: "#cbd5e1", fontSize: "0.85rem" },
              "& .MuiOutlinedInput-root": { "& fieldset": { border: "none" } }
            }}
          />
          <ArrowForward sx={{ color: '#64748b', fontSize: '1rem', display: {xs: 'none', sm: 'block'} }} />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setPage(0);
            }}
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.07)",
              borderRadius: 1,
              input: { color: "white", py: 1, fontSize: "0.85rem", width: '110px' },
              label: { color: "#cbd5e1", fontSize: "0.85rem" },
              "& .MuiOutlinedInput-root": { "& fieldset": { border: "none" } }
            }}
          />
          <Button
            variant="contained"
            startIcon={<Print sx={{ fontSize: "1.1rem !important" }} />}
            onClick={handlePrint}
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.15)",
              color: "white",
              fontSize: "0.8rem",
              fontWeight: 700,
              textTransform: "none",
              py: 1,
              px: 2,
              height: '36px',
              "&:hover": { bgcolor: "rgba(255, 255, 255, 0.25)" },
            }}
          >
            Print
          </Button>
        </Stack>
      </Paper>

      {/* ================= COMPACT STATS SUMMARY METRICS ================= */}
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={6} sm={4} md={2.4}>
          <Card sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "none" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
              Opening Period Liquidity
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} color="text.primary" sx={{ mt: 0.5 }}>
              ₹{openingBalance.toFixed(2)}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2.4}>
          <Card sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "none" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Total Influx (Range)</Typography>
              <TrendingUp color="success" sx={{ fontSize: "1.1rem" }} />
            </Stack>
            <Typography variant="subtitle1" fontWeight={800} color="success.main" sx={{ mt: 0.5 }}>
              +₹{totalIncome.toFixed(2)}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2.4}>
          <Card sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "none" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary" fontWeight={600}>Total Outflow (Range)</Typography>
              <TrendingDown color="error" sx={{ fontSize: "1.1rem" }} />
            </Stack>
            <Typography variant="subtitle1" fontWeight={800} color="error.main" sx={{ mt: 0.5 }}>
              -₹{totalExpense.toFixed(2)}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={6} sm={4} md={2.4}>
          <Card sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "none" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
              Net Period Shift
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} color={netPeriodChange >= 0 ? "success.main" : "error.main"} sx={{ mt: 0.5 }}>
              ₹{netPeriodChange >= 0 ? "+" : ""}{netPeriodChange.toFixed(2)}
            </Typography>
          </Card>
        </Grid>

        <Grid item xs={12} sm={8} md={2.4}>
          <Card sx={{ p: 1.5, borderRadius: 2, bgcolor: "#eff6ff", border: "1px solid #bfdbfe", boxShadow: "none" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="primary.dark" fontWeight={700}>Closing Range Balance</Typography>
              <AccountBalanceWallet color="primary" sx={{ fontSize: "1.1rem" }} />
            </Stack>
            <Typography variant="subtitle1" fontWeight={900} color="primary.main" sx={{ mt: 0.5 }}>
              ₹{computedClosingBalance.toFixed(2)}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* ================= FILTER CONTROLS BAR ================= */}
      <Card className="no-print" sx={{ p: 1.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "none" }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search via source, desc, category..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: "1.1rem" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiInputBase-input": { py: 0.8, fontSize: "0.85rem" } }}
            />
          </Grid>

          <Grid item xs={6} sm={4}>
            <TextField
              size="small"
              select
              fullWidth
              label="Flow Direction"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(0);
              }}
              sx={{ "& .MuiInputBase-root": { height: 34, fontSize: "0.85rem" } }}
            >
              <MenuItem value="All">All Structural Streams</MenuItem>
              <MenuItem value="Income">Inflow Ledger (Income)</MenuItem>
              <MenuItem value="Expense">Outflow Ledger (Expense)</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4}>
            <TextField
              size="small"
              select
              fullWidth
              label="Source Classification"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(0);
              }}
              sx={{ "& .MuiInputBase-root": { height: 34, fontSize: "0.85rem" } }}
            >
              <MenuItem value="All">All Categories</MenuItem>
              <MenuItem value="Fee Payment">Fee Collection</MenuItem>
              <MenuItem value="Direct Income">Direct Inward Income</MenuItem>
              <MenuItem value="Inventory Sale">Inventory Sales</MenuItem>
              <MenuItem value="Wallet Topup">Wallet Topups</MenuItem>
              <MenuItem value="DayBook Expense (Yes)">DayBook Expense (Yes)</MenuItem>
              <MenuItem value="Expense (DayBook No)">Non-DayBook Expense (No)</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Card>

      {/* ================= REPORT GRID TABLE ================= */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "none" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f1f5f9" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, py: 1, fontSize: "0.80rem", width: 60 }}>Sr No</TableCell>
              <TableCell sx={{ fontWeight: 800, py: 1, fontSize: "0.80rem", width: 110 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 800, py: 1, fontSize: "0.80rem", width: 140 }}>Stream Type</TableCell>
              <TableCell sx={{ fontWeight: 800, py: 1, fontSize: "0.80rem" }}>Party / Inward Source</TableCell>
              <TableCell sx={{ fontWeight: 800, py: 1, fontSize: "0.80rem" }}>Narrative / Description</TableCell>
              <TableCell sx={{ fontWeight: 800, py: 1, fontSize: "0.80rem", width: 110 }}>Mode</TableCell>
              <TableCell sx={{ fontWeight: 800, py: 1, fontSize: "0.80rem", textAlign: "right", width: 120 }}>Amount</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: "text.secondary", fontSize: "0.85rem" }}>
                  No historical transactions mapped from {fromDate} to {toDate} matching current setup.
                </TableCell>
              </TableRow>
            ) : (
              filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => (
                  <TableRow key={item.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                    <TableCell sx={{ py: 0.8, fontSize: "0.825rem" }}>
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell sx={{ py: 0.8, fontSize: "0.825rem", whiteSpace: "nowrap" }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
                        <CalendarMonth sx={{ fontSize: "0.9rem" }} />
                        <span>{item.date}</span>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ py: 0.8 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip
                          label={item.type}
                          size="small"
                          color={item.type === "Income" ? "success" : "error"}
                          sx={{ height: 18, fontSize: "0.7rem", fontWeight: 700, borderRadius: 1 }}
                        />
                        <Chip
                          label={item.cat}
                          size="small"
                          variant="outlined"
                          icon={<Category sx={{ fontSize: "0.75rem !important" }} />}
                          sx={{ height: 18, fontSize: "0.68rem", color: "text.secondary", borderColor: "#e2e8f0" }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ py: 0.8, fontWeight: 700, color: "text.primary", fontSize: "0.825rem" }}>
                      {item.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 0.8,
                        fontSize: "0.825rem",
                        color: "text.secondary",
                        maxWidth: 240,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.desc}
                    </TableCell>
                    <TableCell sx={{ py: 0.8 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" color="text.secondary">
                        <Payment sx={{ fontSize: "0.9rem", color: "#64748b" }} />
                        <Typography variant="caption" fontWeight={600} sx={{ color: "#475569" }}>
                          {item.mode}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        py: 0.8,
                        fontWeight: 800,
                        fontSize: "0.85rem",
                        color: item.type === "Income" ? "success.main" : "error.main",
                      }}
                    >
                      {item.type === "Income" ? "+" : "-"}₹{item.amount.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>

        <Divider className="no-print" />

        <TablePagination
          className="no-print"
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": { fontSize: "0.75rem", color: "text.secondary" },
            ".MuiTablePagination-select": { fontSize: "0.75rem" }
          }}
        />
      </TableContainer>
    </Box>
  );
}

export default Incomeexpreport;